import { useState, useEffect, type ChangeEvent, useMemo } from "react"
import { useForm } from "react-hook-form"
import {
  useGetAutocompleteAddressQuery,
  useGetAddressQuery,
  useGetValidateAddressQuery,
} from "@apis/address/useAddress"
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@components/ui/form"
import { Input } from "@components/ui/input"
import { Button } from "@components/ui/button"
import type { IValidateAddressParams } from "@apis/address/types"
import { useUpdateAccountAddressMutation } from "@apis/accounts/useAccounts"
import { toast } from "sonner"
import { Loader2 } from "lucide-react"
import { useQueryClient } from "@tanstack/react-query"
import { addressKeys } from "@apis/address/queryKeys"
import type { IAddressFormData } from "@apis/address/types"
import { MapMarker } from "@components/MapMarker"
import { Skeleton } from "@components/ui/skeleton"
import { useSetProspectAddressMutation } from "@apis/prospects/useProspects"
import type { AxiosError } from "axios"

type TAddressInfoFormProps = {
  // This identifier must be the prospect_guid when address_type_code is "prospect"
  // and the account_id for any other address_type_code
  identifier: string
  // When address_guid is present this is going to be an update UI, otherwise a new address UI
  // (the API always creates a new address)
  address_guid?: string
  address_type_code: "service" | "billing" | "shipping" | "prospect"
  onSave: (address_guid: string) => void
  onCancel: () => void
  showMap?: boolean
}

export const AddressInfoForm = ({
  identifier,
  address_guid,
  address_type_code,
  onSave,
  onCancel,
  showMap = false,
}: TAddressInfoFormProps) => {
  const queryClient = useQueryClient()
  const [manual, setManual] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined)
  const [validateParams, setValidateParams] = useState<
    IValidateAddressParams | undefined
  >(undefined)

  const getAutocompleteAddressQuery = useGetAutocompleteAddressQuery(searchTerm)
  const getValidateAddressQuery = useGetValidateAddressQuery(validateParams)
  const getAddressQuery = useGetAddressQuery(address_guid)
  const setUpdateAccountAddressMutation = useUpdateAccountAddressMutation()
  const setProspectAddressMutation = useSetProspectAddressMutation()

  const EMPTY_ADDRESS_FORM = {
    address: "",
    street: "",
    unit: "",
    city: "",
    state: "",
    zip: "",
    zip4: "",
    deliverable: "",
  }

  const serializeAddressFormData = useMemo(() => {
    if (!address_guid || !getAddressQuery.data) return undefined

    const { street, city, state_code } = getAddressQuery.data
    const addressLine = [street, city, state_code].filter(Boolean).join(", ")

    return {
      address: addressLine,
      street: getAddressQuery.data.street || "",
      unit: getAddressQuery.data.unit || "",
      city: getAddressQuery.data.city || "",
      state: getAddressQuery.data.state_code || "",
      zip: getAddressQuery.data.zip || "",
      zip4: getAddressQuery.data.zip_4 || "",
      deliverable: getAddressQuery.data.deliverability || "",
    }
  }, [address_guid, getAddressQuery.data])

  const coordinates = useMemo(() => {
    const [validAddress] = getValidateAddressQuery.data || []
    if (validAddress?.metadata?.latitude && validAddress?.metadata?.longitude) {
      return {
        latitude: validAddress.metadata.latitude,
        longitude: validAddress.metadata.longitude,
      }
    }

    if (getAddressQuery.data?.latitude && getAddressQuery.data?.longitude) {
      return {
        latitude: parseFloat(getAddressQuery.data.latitude),
        longitude: parseFloat(getAddressQuery.data.longitude),
      }
    }

    return null
  }, [
    getValidateAddressQuery.data,
    getAddressQuery.data?.latitude,
    getAddressQuery.data?.longitude,
  ])

  const form = useForm<IAddressFormData>({
    mode: "onTouched",
    defaultValues: EMPTY_ADDRESS_FORM,
    values: serializeAddressFormData,
  })

  useEffect(() => {
    const [validAddress] = getValidateAddressQuery.data || []
    if (!validAddress) {
      setShowSuccess(false)
      return
    }

    const { components: addressComponents } = validAddress

    if (!addressComponents) return

    const streetLine = [
      addressComponents.primary_number,
      addressComponents.street_name,
      addressComponents.street_suffix,
    ]
      .filter(Boolean)
      .join(" ")

    const addressLine = `${streetLine}, ${addressComponents.city_name} ${addressComponents.state_abbreviation}`

    form.setValue("address", addressLine, { shouldDirty: true })
    form.setValue("street", streetLine, { shouldDirty: true })
    form.setValue("city", addressComponents.city_name, { shouldDirty: true })
    form.setValue("state", addressComponents.state_abbreviation, {
      shouldDirty: true,
    })
    form.setValue("zip", addressComponents.zipcode, { shouldDirty: true })

    form.trigger(["address", "street", "city", "state", "zip"])

    setShowSuccess(true)
    setSearchTerm("")
  }, [getValidateAddressQuery.data, form])

  const onAddressPick = (selected_address: {
    text: string
    street_line: string
    city: string
    state: string
  }) => {
    form.setValue("address", selected_address.text)
    setManual(false)

    queryClient.removeQueries({
      queryKey: addressKeys.details(),
    })

    setValidateParams({
      street: selected_address.street_line,
      city: selected_address.city,
      state: selected_address.state,
    })
  }

  const handleEnterManual = () => {
    form.reset(EMPTY_ADDRESS_FORM, { keepDirty: false, keepTouched: false })
    setShowSuccess(false)
    setSearchTerm("")
    setValidateParams(undefined)
    setManual(!manual)
  }

  const handleSubmitAccountAddress = (formData: IAddressFormData) => {
    setUpdateAccountAddressMutation.mutate(
      {
        account_id: identifier,
        address_type_code,
        data: {
          address: {
            street: formData.street,
            unit: formData.unit,
            city: formData.city,
            state_code: formData.state,
            zip: formData.zip,
            vendor_code: "INSPIRE",
          },
        },
      },
      {
        onSuccess: (response) => onSuccess(response.address_guid),
        onError: (error) => onError(error),
      },
    )
  }

  const handleSubmitProspectAddress = (formData: IAddressFormData) => {
    setProspectAddressMutation.mutate(
      {
        prospect_guid: identifier,
        request: {
          street: formData.street,
          unit: formData.unit,
          city: formData.city,
          state_code: formData.state,
          zip: formData.zip,
        },
      },
      {
        onSuccess: (response) => onSuccess(response.address_guid),
        onError: (error) => onError(error),
      },
    )
  }

  const onSuccess = (address_guid: string) => {
    toast.success("Successfully saved address!")
    form.reset()
    setShowSuccess(false)
    onSave(address_guid)
  }

  const onError = (error: AxiosError) => {
    toast.error(`Error saving address! ${error.message}`)
  }

  const handleSubmit = (formData: IAddressFormData) => {
    return address_type_code === "prospect"
      ? handleSubmitProspectAddress(formData)
      : handleSubmitAccountAddress(formData)
  }

  const AddressSuggestions = () => (
    <ul className="tw:absolute tw:top-full tw:right-0 tw:left-0 tw:z-50 tw:mt-1 tw:max-h-60 tw:overflow-auto tw:rounded-md tw:border tw:bg-white tw:shadow-md">
      {getAutocompleteAddressQuery.data?.map((suggestion, index) => (
        <li
          key={index}
          className="hover:tw:bg-gray-100 tw:cursor-pointer tw:p-2 tw:underline"
          onClick={() => {
            onAddressPick(suggestion)
            setSearchTerm("")
          }}
        >
          {suggestion.text}
        </li>
      ))}
    </ul>
  )

  const handleOnChangeAddress = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === "" || e.target.value.length < 3) return
    setSearchTerm(e.target.value)
  }

  return (
    <Form {...form}>
      <div className="tw:flex tw:flex-col tw:gap-4">
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="tw:grid tw:grid-cols-12 tw:items-baseline tw:gap-4"
        >
          <div className="tw:col-span-12">
            <FormField
              control={form.control}
              name="address"
              rules={{ required: !manual ? "Address is required" : false }}
              render={({ field }) => (
                <FormItem className="tw:relative">
                  <FormLabel>Address</FormLabel>
                  <FormControl>
                    <div className="tw:grid tw:grid-cols-12 tw:gap-4">
                      <div className="tw:col-span-12 tw:md:col-span-6">
                        <Input
                          placeholder="Type Address Here..."
                          {...field}
                          autoComplete="off"
                          onChange={(e) => {
                            field.onChange(e)
                            handleOnChangeAddress(e)
                          }}
                        />
                      </div>
                      <div className="tw:col-span-12 tw:md:col-span-6">
                        <Button
                          variant="outline"
                          type="button"
                          onClick={handleEnterManual}
                        >
                          Enter Address Manually
                        </Button>
                      </div>
                    </div>
                  </FormControl>
                  <FormMessage>
                    {showSuccess && (
                      <p className="tw:text-green-600">
                        Lookup Successful! Address fields populated.
                      </p>
                    )}
                  </FormMessage>
                  {getAutocompleteAddressQuery.data?.length && (
                    <AddressSuggestions />
                  )}
                </FormItem>
              )}
            />
          </div>

          <div className="tw:col-span-12 tw:md:col-span-6">
            <FormField
              control={form.control}
              name="street"
              rules={{ required: "Street is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Street</FormLabel>
                  <FormControl>
                    <Input {...field} disabled={!manual} autoComplete="off" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="tw:col-span-12 tw:md:col-span-6">
            <FormField
              control={form.control}
              name="unit"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Unit</FormLabel>
                  <FormControl>
                    <Input {...field} autoComplete="off" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="tw:col-span-12 tw:md:col-span-6">
            <FormField
              control={form.control}
              name="city"
              rules={{ required: "City is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>City</FormLabel>
                  <FormControl>
                    <Input {...field} disabled={!manual} autoComplete="off" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="tw:col-span-12 tw:md:col-span-6">
            <FormField
              control={form.control}
              name="state"
              rules={{ required: "State is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>State</FormLabel>
                  <FormControl>
                    <Input {...field} disabled={!manual} autoComplete="off" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="tw:col-span-12 tw:md:col-span-6">
            <FormField
              control={form.control}
              name="zip"
              rules={{ required: "Zip is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Zip</FormLabel>
                  <FormControl>
                    <Input {...field} disabled={!manual} autoComplete="off" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="tw:col-span-12 tw:md:col-span-6">
            <FormField
              control={form.control}
              name="zip4"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Zip 4</FormLabel>
                  <FormControl>
                    <Input {...field} disabled={!manual} autoComplete="off" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="tw:col-span-12 tw:md:col-span-6">
            <FormField
              control={form.control}
              name="deliverable"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Deliverable</FormLabel>
                  <FormControl>
                    <Input {...field} disabled={!manual} autoComplete="off" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="tw:col-span-12 tw:flex tw:justify-end tw:gap-4">
            <Button
              type="submit"
              disabled={
                !form.formState.isDirty ||
                !form.formState.isValid ||
                setUpdateAccountAddressMutation.isPending ||
                setProspectAddressMutation.isPending
              }
            >
              {setUpdateAccountAddressMutation.isPending ||
              setProspectAddressMutation.isPending ? (
                <>
                  <Loader2 className="tw:animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <span>Save</span>
              )}
            </Button>
            <Button variant="outline" onClick={() => onCancel()}>
              Cancel
            </Button>
          </div>
        </form>
        {showMap && (
          <>
            {(address_guid && getAddressQuery.isPending) ||
            getValidateAddressQuery.isFetching ? (
              <Skeleton className="tw:h-[300px] tw:rounded-md" />
            ) : (
              coordinates && (
                <MapMarker
                  lat={coordinates.latitude}
                  long={coordinates.longitude}
                />
              )
            )}
          </>
        )}
      </div>
    </Form>
  )
}
